// src/services/api.js
import axios from 'axios';

// Get environment variables with fallbacks and debug
const STRAPI_API_URL = process.env.REACT_APP_STRAPI_API_URL;
const STRAPI_API_TOKEN = process.env.REACT_APP_STRAPI_API_TOKEN;

// Log environment variables for debugging (remove in production)
console.log('Using Strapi API URL:', STRAPI_API_URL);
console.log('API Token exists:', !!STRAPI_API_TOKEN);

// Check for missing configuration
if (!STRAPI_API_URL) {
  console.error('ERROR: Missing REACT_APP_STRAPI_API_URL environment variable');
}

if (!STRAPI_API_TOKEN) {
  console.error('ERROR: Missing REACT_APP_STRAPI_API_TOKEN environment variable');
}

// Create a base axios instance with common configuration
const api = axios.create({
  baseURL: STRAPI_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Authorization': STRAPI_API_TOKEN ? `Bearer ${STRAPI_API_TOKEN}` : ''
  }
});

// Export API functions
export const apiService = {
  /**
   * Fetch records from a collection with sorting and pagination
   * @param {string} collection - The collection name
   * @param {number} limit - The maximum number of records to fetch
   * @returns {Promise} - The API response
   */
  fetchCollection: async (collection, limit = 100) => {
    try {
      const response = await api.get(`/${collection}`, {
        params: {
          'populate': '*',
          'pagination[limit]': limit,
          'sort[0]': 'createdAt:desc' // Sort by creation date, newest first
        }
      });

      return { data: response.data, error: null };
    } catch (error) {
      console.error(`Error fetching ${collection}:`, error);
      return {
        data: null,
        error: error.response?.data?.error || error.message || 'Unknown error'
      };
    }
  },

  /**
   * Update a record in a collection
   * @param {string} collection - The collection name
   * @param {number|string} id - The record ID to update
   * @param {object} data - The data to update
   * @returns {Promise} - The API response
   */
  updateRecord: async (collection, id, data) => {
    try {
      const response = await api.put(`/${collection}/${id}`, {
        data: data
      });
      return { success: true, data: response.data, error: null };
    } catch (error) {
      console.error(`Error updating record in ${collection}:`, error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.error || error.message || 'Unknown error'
      };
    }
  },

  /**
   * Delete a record from a collection
   * @param {string} collection - The collection name
   * @param {number|string} id - The record ID to delete
   * @returns {Promise} - The API response
   */
  deleteRecord: async (collection, id) => {
    try {
      const response = await api.delete(`/${collection}/${id}`);
      return { success: true, data: response.data, error: null };
    } catch (error) {
      console.error(`Error deleting record from ${collection}:`, error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.error || error.message || 'Unknown error'
      };
    }
  },

  /**
   * Test API connection
   * @returns {Promise} - API connection status
   */
  testConnection: async () => {
    try {
      // Try to fetch a minimal amount of data to test connection
      await api.get('/self-assessment-cases', {
        params: { 'pagination[limit]': 1 }
      });
      return { success: true, message: 'Connection successful' };
    } catch (error) {
      console.error('API Connection test failed:', error);
      // If there's a 403 error, we might still have partial access
      if (error.response?.status === 403) {
        return {
          success: true,
          message: 'Limited access API connection (some endpoints may work)'
        };
      }

      return {
        success: false,
        message: error.response?.data?.error?.message || error.message || 'Connection failed'
      };
    }
  }
};

export default apiService;